import { AuthButtonSpinner } from "@layouts/betto/authPopup/components/AuthModal/elements.js";
import { AuthCloseIcon } from "@layouts/betto/authPopup/icons/AuthCloseIcon.jsx";
import { DiceIcon, PlayNowIcon, TryAgainIcon } from "@pages/casino/assets/icons";
import {
	getImageSrc,
	handleBettoGameImageError,
} from "@pages/home/betto/casinoGridSlider/gameCard.jsx";
import { getBettoDefaultGameIcon } from "@pages/home/betto/utils";
import React, { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Translate } from "react-localize-redux";

import useController from "../useController.js";
import {
	BettoLuckyActions,
	BettoLuckyAffiliate,
	BettoLuckyBody,
	BettoLuckyCloseButton,
	BettoLuckyDetails,
	BettoLuckyGameName,
	BettoLuckyHeader,
	BettoLuckyImageWrap,
	BettoLuckyLoaderWrap,
	BettoLuckyOverlay,
	BettoLuckyPanel,
	BettoLuckyPlayButton,
	BettoLuckyRoot,
	BettoLuckyTitle,
	BettoLuckyTrigger,
	BettoLuckyTryAgainButton,
} from "./elements.js";

const BettoIFeelLuckyModal = ({ labelKey = "betto.casino.iFeelLucky" }) => {
	const {
		state: { isToggled, randomGame, casinoGames, isLoading },
		handlers: { handleToggleChange, handlePlayButtonClick, handleGameSelect },
	} = useController();

	useEffect(() => {
		if (isToggled) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isToggled]);

	const gameImageSrc = randomGame ? getImageSrc(randomGame) : getBettoDefaultGameIcon();

	const handleImageError = useCallback(
		(event) => {
			if (!randomGame) return;
			handleBettoGameImageError(event, randomGame);
		},
		[randomGame]
	);

	const showContent = casinoGames.length > 0 && randomGame;
	const showLoader = isToggled && (isLoading || !showContent);

	return (
		<Translate>
			{({ translate }) => (
				<>
					<BettoLuckyTrigger
						type="button"
						data-testid="button-casino-filter-i-feel-lucky"
						onClick={handleToggleChange}>
						<DiceIcon />
						<span>{translate(labelKey)}</span>
					</BettoLuckyTrigger>

					{isToggled &&
						typeof document !== "undefined" &&
						createPortal(
							<BettoLuckyRoot $open>
								<BettoLuckyOverlay
									type="button"
									aria-label="Close lucky modal"
									onClick={handleToggleChange}
								/>
								<BettoLuckyPanel onClick={(event) => event.stopPropagation()}>
									<BettoLuckyHeader>
										<BettoLuckyTitle data-testid="text-lucky-modal-title">
											{translate("YOU_ARE_LUCKY")}
										</BettoLuckyTitle>
										<BettoLuckyCloseButton
											type="button"
											data-testid="button-lucky-modal-close"
											aria-label="Close"
											onClick={handleToggleChange}>
											<AuthCloseIcon />
										</BettoLuckyCloseButton>
									</BettoLuckyHeader>

									{showLoader ? (
										<BettoLuckyLoaderWrap aria-hidden="true">
											<AuthButtonSpinner />
										</BettoLuckyLoaderWrap>
									) : (
										showContent && (
											<BettoLuckyBody>
												<BettoLuckyImageWrap>
													<img
														key={randomGame?.id}
														data-testid="image-lucky-modal-game"
														src={gameImageSrc}
														alt={randomGame?.remote_name || ""}
														onError={handleImageError}
													/>
												</BettoLuckyImageWrap>

												<BettoLuckyDetails>
													{randomGame?.affiliate && (
														<BettoLuckyAffiliate>
															{randomGame.affiliate}
														</BettoLuckyAffiliate>
													)}
													<BettoLuckyGameName data-testid="text-lucky-modal-game-name">
														{randomGame?.remote_name}
													</BettoLuckyGameName>

													<BettoLuckyActions>
														<BettoLuckyPlayButton
															type="button"
															data-testid="button-lucky-modal-play"
															onClick={handlePlayButtonClick}>
															<PlayNowIcon />
															{translate("PLAY_NOW")}
														</BettoLuckyPlayButton>
														<BettoLuckyTryAgainButton
															type="button"
															data-testid="button-lucky-modal-try-again"
															onClick={handleGameSelect}>
															<TryAgainIcon />
															{translate("TRY_AGAIN")}
														</BettoLuckyTryAgainButton>
													</BettoLuckyActions>
												</BettoLuckyDetails>
											</BettoLuckyBody>
										)
									)}
								</BettoLuckyPanel>
							</BettoLuckyRoot>,
							document.body
						)}
				</>
			)}
		</Translate>
	);
};

export default BettoIFeelLuckyModal;
